import { Suspense, lazy, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "./App.css";
import MainLoader from "./Components/_widgets/Loader/MainLoader";
import AlertMedicine from "./Components/Alert/AlertMedicine";
import BeforeAfter from "./Components/BeforeAfter/BeforeAfter";
import CreditNotes from "./Components/CreditNotes/CreditNotes";
import Applications from "./Components/DataManagement/Applications/Applications";
import DignosisTypes from "./Components/DataManagement/Diagnosis/DiagnosisType";
import InventoryCategory from "./Components/DataManagement/InventoryCategory/InventoryCategory";
import MedicineCategory from "./Components/DataManagement/MedicineCategory/MedicineCategory";
import MedicineCompany from "./Components/DataManagement/MedicineCompany/MedicineCompany";
import DigitalPro from "./Components/DigitalPro/DigitalPro";
import Expense from "./Components/Expense/Expense/Expense";
import ExpenseCategory from "./Components/Expense/ExpenseCategory/ExpenseCategory";
import Inventory from "./Components/Inventory/Inventory";
import MedicineBill from "./Components/MedicineBill/MedicineBill";
import NotificationLogs from "./Components/NotificationLogs/NotificationLogs";
import TrackPatient from "./Components/Patients/TrackPatient/TrackPatient";
import PrivacyPolicy from "./Components/PrivacyPolicy/PrivacyPolicy";
import Treatments from "./Components/Treatment/Treatment";
import WhatsAppInbox from "./Components/WhatsAppInbox/WhatsAppInbox";
import TemplateDetails from "./Components/WhatsappSchedule/Template/TemplateDetails";
import { CONFIGS, CUSTOM_BILLING_ITEM, DAY_NIGHT, MODULES } from "./Constants/appConstants";
import * as storageConstants from "./Constants/storageConstant";
import { IMAGE_URL } from "./Constants/urlConstants";
import ProtectedRoute from "./Protected/Protected";
import { getProfile } from "./Services/ApiHelper";
import * as commonServices from "./Services/common";
import "./Services/instrument";
import { isNullOrEmpty } from "./Utils/stringUtils";
import { warn } from "./Wrapper/toast/toast";

const NotFound = lazy(() => import("./Components/NotFound/NotFound"));
const LoginLayout = lazy(() => import("./Components/Layouts/LoginLayout"));
const HomeLayout = lazy(() => import("./Components/Layouts/HomeLayout"));
const Login = lazy(() => import("./Components/Login/Login"));
const ChangePassword = lazy(() => import("./Components/ChangePassword/ChangePassword"));
const ResetPassword = lazy(() => import("./Components/ResetPassword/ResetPassword"));
const Dashboard = lazy(() => import("./Components/Dashboard/Dashboard"));
const PatientVisits = lazy(() => import("./Components/Patients/PatientVisits/PatientVisits"));
const PatientVisitDetail = lazy(
  () => import("./Components/Patients/PatientVisits/PatientVisitDetail"),
);
const Details = lazy(() => import("./Components/Patients/PatientList/PatientDetail"));
const Users = lazy(() => import("./Components/Users/Users"));
const MedicinesList = lazy(() => import("./Components/Medicines/MedicinesList/MedicinesList"));
const PurchaseMedicines = lazy(
  () => import("./Components/Medicines/PurchaseMedicines/PurchaseMedicines"),
);
const MedicineAudit = lazy(() => import("./Components/Medicines/MedicineAudit/MedicineAudit"));
const SaleReturn = lazy(() => import("./Components/SaleReturn/SaleReturn"));
const Reports = lazy(() => import("./Components/Reports/Reports"));
const ViewReport = lazy(() => import("./Components/Reports/ViewReport"));
const Help = lazy(() => import("./Components/Help/Help"));
const Suppliers = lazy(() => import("./Components/DataManagement/Suppliers/Suppliers"));
const Departments = lazy(() => import("./Components/DataManagement/Departments/Departments"));

const Medicines = lazy(() => import("./Components/DataManagement/Medicines/Medicines"));
const MedicineTypes = lazy(() => import("./Components/DataManagement/MedicineType/MedicineType"));
const Services = lazy(() => import("./Components/DataManagement/Services/Services"));
const ServiceTypes = lazy(() => import("./Components/DataManagement/ServiceType/ServiceType"));
const Export = lazy(() => import("./Components/DataManagement/Export/Export"));
const BillingItems = lazy(() => import("./Components/DataManagement/BillingItems/BillingItems"));
const Attendance = lazy(() => import("./Components/Attendance/Attendance"));
const Rooms = lazy(() => import("./Components/Rooms/Rooms"));
const Beds = lazy(() => import("./Components/Beds/Beds"));
const IpdManagement = lazy(() => import("./Components/IPD/IpdManagement"));
const EditIpdManagement = lazy(() => import("./Components/IPD/EditIpd"));
const Alert = lazy(() => import("./Components/Alert/Alert"));
const CalendarView = lazy(() => import("./Components/Patients/Calendar/Calander"));
const NextVisit = lazy(() => import("./Components/Patients/NextVisitListing/NextVisit"));
const WhatsappSchedule = lazy(() => import("./Components/WhatsappSchedule/WhatsappSchedule"));
const AddSchedule = lazy(() => import("./Components/WhatsappSchedule/AddSchedule"));
const AddWhatsappTemplate = lazy(
  () => import("./Components/WhatsappSchedule/Template/AddTemplate"),
);
const Template = lazy(() => import("./Components/WhatsappSchedule/Template/Template"));
const SmsSchedule = lazy(() => import("./Components/SmsSchedule/SmsSchedule"));
const AddSmsSchedule = lazy(() => import("./Components/SmsSchedule/AddSchedule"));
const AddSmsTemplate = lazy(() => import("./Components/SmsSchedule/Template/AddTemplate"));
const SmsTemplate = lazy(() => import("./Components/SmsSchedule/Template/Template"));
const WalletDashboard = lazy(() => import("./Components/Wallet/WalletDashboard"));
const PatientListUi = lazy(() => import("./Components/Patients/PatientList/PatientList"));
const NotificationPartner = lazy(
  () => import("./Components/NotificationPartner/NotificationPartner"),
);
const Notifications = lazy(() => import("./Components/Notifications/Notifications"));
const PatientVisitDetails = lazy(() => import("./Components/PatientVisit/PatientVisitDetails.tsx"));
const PaymentTracker = lazy(() => import("./Components/PaymentTracker/PaymentTracker"));
const ExpiryTracker = lazy(() => import("./Components/ExpiryTracker/ExpiryTracker"));

function App() {
  const location = useLocation();
  const [loading, setLoading] = useState({ main: false, profile: false });

  // Refresh SYSTEM_DATA on every full page load (App mount).
  // Master catalogs (custom_billing_item, medicines, services, etc.)
  // are mutated by other users on other machines; without this,
  // user 2 only sees user 1's additions after a logout/login because
  // the route-pathname effect below skips the refetch when localStorage
  // already has SYSTEM_DATA. F5 / tab reopen / first mount now picks
  // up cross-user changes transparently. Empty deps → runs once per
  // App mount, NOT on every internal route change (which would be
  // too chatty for /api/system_data).
  useEffect(() => {
    const auth = commonServices.getLocalData(storageConstants.AUTH);
    if (auth) {
      commonServices.fetchSystemData((state) => setLoading((prev) => ({ ...prev, main: state })));
    }
  }, []);

  useEffect(() => {
    const data = commonServices.getLocalData(storageConstants.MASTER_DATA);

    if (isNullOrEmpty(data)) {
      commonServices.fetchMasterData((state) => setLoading({ ...loading, main: state }));
    }

    window.scrollTo(0, 0);

    const lastVisitedPage = commonServices.getLocalData(storageConstants.LAST_VISITED_PAGE) || {};

    if (!location.pathname.includes("patient")) {
      commonServices.storeLocalData(storageConstants.LAST_VISITED_PAGE, {
        ...lastVisitedPage,
        patient: null,
      });
    } else if (!location.pathname.includes("report")) {
      commonServices.storeLocalData(storageConstants.LAST_VISITED_PAGE, {
        ...lastVisitedPage,
        report: null,
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    commonServices.getLocalData(storageConstants.AUTH) && getProfileDetails();
  }, [commonServices.getLocalData(storageConstants.AUTH)]);

  useEffect(() => {
    const color = commonServices.getLocalData(storageConstants.HOSPITAL_DATA)?.color;
    !isNullOrEmpty(color) && commonServices.setPrimarySecondaryColors(color);
  }, [commonServices.getLocalData(storageConstants.HOSPITAL_DATA)?.color]);

  const getProfileDetails = () => {
    setLoading((prev) => ({ ...prev, profile: true }));
    getProfile()
      .then((response) => {
        if (response?.isSuccess) {
          const user_data = {
            name: response?.data?.name,
            role: response?.data?.role,
            id: response?.data?.id,
            // Per-user billing-access flags — kept in sync with the
            // login response shape so a profile refresh after an
            // admin edits the user reflects the new toggle state
            // without forcing a re-login.
            access_medicines: response?.data?.access_medicines !== 0,
            access_services: response?.data?.access_services !== 0,
            access_claim_items: response?.data?.access_claim_items !== 0,
            access_all_patients: response?.data?.access_all_patients !== 0,
            hospital_unit_id: response?.data?.hospital_unit_id || null,
            hospital_unit: response?.data?.hospital_unit || null,
          };
          const hospital_data = {
            id: response?.data?.hospital_id,
            name: response?.data?.hospital?.name,
            logo: response?.data?.hospital?.logo
              ? IMAGE_URL + response?.data?.hospital?.logo
              : null,
            color: response?.data?.hospital?.primary_color,
            address: response?.data?.hospital?.address,
            mno: response?.data?.hospital?.mno,
            expiry_date: response?.data?.hospital?.expiry_date,
            services: response?.data?.hospital?.hospital_services,
            single_entry: response?.data?.hospital?.single_entry,
            hospital_units: response?.data?.hospital?.hospital_units,
          };

          commonServices.storeLocalData(storageConstants.USER_DATA, user_data);
          commonServices.storeLocalData(
            storageConstants.PERMISSIONS,
            response?.data?.hospital?.permissions,
          );
          commonServices.storeLocalData(storageConstants.CONFIG, response?.data?.hospital?.config);
          commonServices.storeLocalData(
            storageConstants.INVESTIGATION,
            response?.data?.hospital?.investigation,
          );
          commonServices.storeLocalData(
            storageConstants.PRESCRIPTION_DOCTORS,
            response?.data?.hospital?.hospital_doctor,
          );
          commonServices.storeLocalData(storageConstants.HOSPITAL_DATA, hospital_data);
          commonServices.storeLocalData(
            storageConstants.HOSPITAL_NOTIFICATION_CHANNELS,
            response?.data?.hospital?.hospital_notification_channels,
          );
        } else {
          warn(response?.message);
        }
        setLoading((prev) => ({ ...prev, profile: false }));
      })
      .catch(() => {
        setLoading((prev) => ({ ...prev, profile: false }));
      });
  };
  const applicationConfigControl = {
    label: CONFIGS.PRESCRIPTION_DOSE_DROPDOWN,
    value: DAY_NIGHT,
  };

  const {
    DASHBOARD_MODULE,
    MEDICINES_MODULE,
    SERVICIES_MODULE,
    REPORTS_MODULE,
    ATTENDANCE_MODULE,
    VIEW_EXPANSE,
    ALERT,
    MARKETING,
    SERVICE_ALERT,
    PATIENT_VISIT_ALERT,
    TELECONSULT,
    VIEW_IPD,
    PATIENT_LISTING,
    DIAGNOSIS,
    BEFORE_AFTER,
    TREATMENT,
    TRACK_REPORT,
    VIEW_INVENTORY,
    VIEW_MEDICINE_SALE_RETURN,
    VIEW_MEDICINE_PURCHASE_RETURN,
  } = MODULES;

  return (
    <div className="App">
      {process.env.REACT_APP_ENV !== "production" && (
        <div className="ribbon ribbon-top-left">
          <span>{process.env.REACT_APP_ENV}</span>
        </div>
      )}

      <ToastContainer style={{ zIndex: 999999 }} />

      <Suspense fallback={<MainLoader />}>
        {loading.main || loading.profile ? (
          <MainLoader />
        ) : (
          <Routes>
            <Route element={<LoginLayout />}>
              <Route
                exact
                path="/"
                element={<ProtectedRoute Component={<Login location={location} />} />}
              />
              <Route exact path="/reset-password" element={<ResetPassword location={location} />} />
              <Route exact path="/privacy-policy" element={<PrivacyPolicy location={location} />} />
              <Route exact path="/*" element={<NotFound />} />
            </Route>

            <Route element={<HomeLayout />}>
              <Route
                exact
                path="/change-password"
                element={
                  <ProtectedRoute pageName="/change-password" Component={<ChangePassword />} />
                }
              />
              <Route
                exact
                path="/dashboard"
                element={
                  <ProtectedRoute
                    pageName="/dashboard"
                    moduleName={DASHBOARD_MODULE}
                    Component={<Dashboard />}
                  />
                }
              />
              <Route
                exact
                path="/patients"
                element={<ProtectedRoute pageName="/patients" Component={<PatientVisits />} />}
              />
              <Route
                exact
                path="/patient-list"
                element={
                  <ProtectedRoute
                    pageName="/patient-list"
                    moduleName={PATIENT_LISTING}
                    Component={<PatientListUi />}
                  />
                }
              />
              <Route
                exact
                path="/next-visit"
                element={
                  <ProtectedRoute
                    pageName="/next-visit"
                    configControl={CONFIGS.NEXT_VISIT_LISTING}
                    Component={<NextVisit />}
                  />
                }
              />
              <Route
                exact
                path="/track-patient"
                element={
                  <ProtectedRoute
                    pageName="/track-patient"
                    moduleName={TRACK_REPORT}
                    Component={<TrackPatient />}
                  />
                }
              />
              <Route
                exact
                path="/calendar"
                element={
                  <ProtectedRoute
                    configControl={CONFIGS.CALENDAR_VIEW}
                    pageName="/calendar"
                    Component={<CalendarView />}
                  />
                }
              />
              <Route
                exact
                path="/v1/patient-visit/:visitId"
                element={
                  <ProtectedRoute pageName="/patient-visit" Component={<PatientVisitDetail />} />
                }
              />
              <Route
                exact
                path="/patient-visit/:visitId/:tab?"
                element={
                  <ProtectedRoute
                    pageName="/v2/patient-visit"
                    Component={<PatientVisitDetails />}
                  />
                }
              />
              <Route
                exact
                path="/patient-details/:visitId"
                element={<ProtectedRoute pageName="/patient-details" Component={<Details />} />}
              />
              <Route
                exact
                path="/users"
                element={<ProtectedRoute pageName="/users" Component={<Users />} />}
              />
              <Route
                exact
                path="/departments"
                element={<ProtectedRoute pageName="/departments" Component={<Departments />} />}
              />
              <Route
                exact
                path="/suppliers"
                element={
                  <ProtectedRoute
                    pageName="/suppliers"
                    moduleName={MEDICINES_MODULE}
                    Component={<Suppliers />}
                  />
                }
              />
              <Route
                exact
                path="/services"
                element={
                  <ProtectedRoute
                    pageName="/services"
                    moduleName={SERVICIES_MODULE}
                    Component={<Services />}
                  />
                }
              />
              <Route
                exact
                path="/billing-items"
                element={
                  <ProtectedRoute
                    pageName="/billing-items"
                    moduleName={CUSTOM_BILLING_ITEM}
                    Component={<BillingItems />}
                  />
                }
              />
              <Route
                exact
                path="/service-type"
                element={
                  <ProtectedRoute
                    pageName="/service-type"
                    moduleName={SERVICIES_MODULE}
                    Component={<ServiceTypes />}
                  />
                }
              />
              <Route
                exact
                path="/medicine-type"
                element={
                  <ProtectedRoute
                    pageName="/medicine-type"
                    moduleName={MEDICINES_MODULE}
                    Component={<MedicineTypes />}
                  />
                }
              />
              <Route
                exact
                path="/medicine-category"
                element={
                  <ProtectedRoute
                    pageName="/medicine-category"
                    moduleName={MEDICINES_MODULE}
                    Component={<MedicineCategory />}
                  />
                }
              />
              <Route
                exact
                path="/medicine-company"
                element={
                  <ProtectedRoute
                    pageName="/medicine-company"
                    moduleName={MEDICINES_MODULE}
                    Component={<MedicineCompany />}
                  />
                }
              />
              <Route
                exact
                path="/inventory-category"
                element={
                  <ProtectedRoute
                    pageName="/inventory-category"
                    moduleName={VIEW_INVENTORY}
                    Component={<InventoryCategory />}
                  />
                }
              />
              <Route
                exact
                path="/meds"
                element={<ProtectedRoute pageName="/meds" Component={<Medicines />} />}
              />
              <Route
                exact
                path="/medicines"
                element={
                  <ProtectedRoute
                    pageName="/medicines"
                    moduleName={MEDICINES_MODULE}
                    Component={<MedicinesList />}
                  />
                }
              />
              <Route
                exact
                path="/purchase-medicines"
                element={
                  <ProtectedRoute
                    pageName="/purchase-medicines"
                    moduleName={MEDICINES_MODULE}
                    Component={<PurchaseMedicines />}
                  />
                }
              />
              <Route
                exact
                path="/medicine-audit"
                element={
                  <ProtectedRoute
                    pageName="/medicine-audit"
                    moduleName={MEDICINES_MODULE}
                    Component={<MedicineAudit />}
                  />
                }
              />
              <Route
                exact
                path="/sale-return"
                element={
                  <ProtectedRoute
                    pageName="/sale-return"
                    moduleName={VIEW_MEDICINE_SALE_RETURN}
                    Component={<SaleReturn />}
                  />
                }
              />
              <Route
                exact
                path="/reports"
                element={
                  <ProtectedRoute
                    pageName="/reports"
                    moduleName={REPORTS_MODULE}
                    Component={<Reports />}
                  />
                }
              />
              <Route path="/due-tracker" element={<Navigate to="/payment-tracker" replace />} />
              <Route
                exact
                path="/payment-tracker"
                element={
                  <ProtectedRoute
                    pageName="/payment-tracker"
                    moduleName={MODULES.DUE_TRACKER}
                    Component={<PaymentTracker />}
                  />
                }
              />
              <Route
                exact
                path="/expiry-tracker"
                element={
                  <ProtectedRoute
                    pageName="/expiry-tracker"
                    moduleName={MODULES.EXPIRY_TRACKER}
                    Component={<ExpiryTracker />}
                  />
                }
              />
              <Route
                exact
                path="/rooms"
                element={
                  <ProtectedRoute pageName="/rooms" moduleName={VIEW_IPD} Component={<Rooms />} />
                }
              />
              <Route
                exact
                path="/beds"
                element={
                  <ProtectedRoute pageName="/beds" moduleName={VIEW_IPD} Component={<Beds />} />
                }
              />
              <Route
                exact
                path="/ipd-management"
                element={
                  <ProtectedRoute
                    pageName="/ipd-management"
                    moduleName={VIEW_IPD}
                    Component={<IpdManagement />}
                  />
                }
              />
              <Route
                exact
                path="/ipd-management-edit/:visitId"
                element={
                  <ProtectedRoute
                    pageName="/ipd-management-edit"
                    moduleName={VIEW_IPD}
                    Component={<EditIpdManagement />}
                  />
                }
              />
              <Route
                exact
                path="/alert"
                element={
                  <ProtectedRoute pageName="/alert" moduleName={ALERT} Component={<Alert />} />
                }
              />
              <Route
                exact
                path="/alert-medicine"
                element={
                  <ProtectedRoute
                    pageName="/alert"
                    moduleName={VIEW_IPD}
                    Component={<AlertMedicine />}
                  />
                }
              />
              <Route
                exact
                path="/digital-pro"
                element={<ProtectedRoute pageName="/digital-pro" Component={<DigitalPro />} />}
              />
              <Route
                exact
                path="/view-report"
                element={
                  <ProtectedRoute
                    pageName="/reports"
                    moduleName={REPORTS_MODULE}
                    Component={<ViewReport />}
                  />
                }
              />
              <Route
                exact
                path="/export"
                element={<ProtectedRoute pageName="/export" Component={<Export />} />}
              />
              <Route
                exact
                path="/notification-partners"
                element={
                  <ProtectedRoute
                    pageName="/whastappschedule"
                    moduleName={[ALERT, MARKETING, SERVICE_ALERT, PATIENT_VISIT_ALERT, TELECONSULT]}
                    Component={<NotificationPartner />}
                  />
                }
              />
              <Route
                exact
                path="/notifications"
                element={
                  <ProtectedRoute
                    pageName="/whastappschedule"
                    moduleName={[ALERT, MARKETING, SERVICE_ALERT, PATIENT_VISIT_ALERT, TELECONSULT]}
                    Component={<Notifications />}
                  />
                }
              />
              <Route
                exact
                path="/notification-logs"
                element={
                  <ProtectedRoute
                    pageName="/whastappschedule"
                    moduleName={[ALERT, MARKETING, SERVICE_ALERT, PATIENT_VISIT_ALERT, TELECONSULT]}
                    Component={<NotificationLogs />}
                  />
                }
              />
              <Route
                exact
                path="/attendance"
                element={
                  <ProtectedRoute
                    pageName="/attendance"
                    moduleName={ATTENDANCE_MODULE}
                    Component={<Attendance />}
                  />
                }
              />
              <Route
                exact
                path="/help"
                element={<ProtectedRoute pageName="/help" Component={<Help />} />}
              />
              <Route
                exact
                path="/expense-category"
                element={
                  <ProtectedRoute
                    pageName="/expense-category"
                    moduleName={VIEW_EXPANSE}
                    Component={<ExpenseCategory />}
                  />
                }
              />
              <Route
                exact
                path="/expense"
                element={
                  <ProtectedRoute
                    moduleName={VIEW_EXPANSE}
                    pageName="/expense"
                    Component={<Expense />}
                  />
                }
              />
              <Route
                exact
                path="/whatsapp-inbox"
                element={
                  <ProtectedRoute
                    moduleName={MARKETING}
                    pageName="/whatsapp-inbox"
                    Component={<WhatsAppInbox />}
                  />
                }
              />
              <Route
                exact
                path="/inventory"
                element={
                  <ProtectedRoute
                    moduleName={VIEW_INVENTORY}
                    pageName="/inventory"
                    Component={<Inventory />}
                  />
                }
              />
              ``
              <Route
                exact
                path="/whastappschedule"
                element={
                  <ProtectedRoute
                    pageName="/whastappschedule"
                    moduleName={MARKETING}
                    Component={<WhatsappSchedule />}
                  />
                }
              />
              <Route
                exact
                path="/add-whastappschedule"
                element={
                  <ProtectedRoute
                    pageName="/add-whastappschedule"
                    moduleName={MARKETING}
                    Component={<AddSchedule />}
                  />
                }
              />
              <Route
                exact
                path="/add-whatsapp-template"
                element={
                  <ProtectedRoute
                    pageName="/add-whatsapp-template"
                    moduleName={MARKETING}
                    Component={<AddWhatsappTemplate />}
                  />
                }
              />
              <Route
                exact
                path="/template"
                element={
                  <ProtectedRoute
                    pageName="/template"
                    moduleName={MARKETING}
                    Component={<Template />}
                  />
                }
              />
              <Route
                exact
                path="/template/details"
                element={
                  <ProtectedRoute
                    pageName="/template"
                    moduleName={MARKETING}
                    Component={<TemplateDetails />}
                  />
                }
              />
              <Route
                exact
                path="/smsschedule"
                element={
                  <ProtectedRoute
                    pageName="/smsschedule"
                    moduleName={MARKETING}
                    Component={<SmsSchedule />}
                  />
                }
              />
              <Route
                exact
                path="/add-smsschedule"
                element={
                  <ProtectedRoute
                    pageName="/add-smsschedule"
                    moduleName={MARKETING}
                    Component={<AddSmsSchedule />}
                  />
                }
              />
              <Route
                exact
                path="/sms-template"
                element={
                  <ProtectedRoute
                    pageName="/sms-template"
                    moduleName={MARKETING}
                    Component={<SmsTemplate />}
                  />
                }
              />
              <Route
                exact
                path="/add-sms-template"
                element={
                  <ProtectedRoute
                    pageName="/add-sms-template"
                    moduleName={MARKETING}
                    Component={<AddSmsTemplate />}
                  />
                }
              />
              <Route
                exact
                path="/diagnosis"
                element={
                  <ProtectedRoute
                    pageName="/diagnosis"
                    moduleName={DIAGNOSIS}
                    Component={<DignosisTypes />}
                  />
                }
              />
              <Route
                exact
                path="/wallet"
                element={
                  <ProtectedRoute
                    pageName="/wallet"
                    moduleName={[MARKETING, ALERT]}
                    Component={<WalletDashboard />}
                  />
                }
              />
              <Route
                exact
                path="/applications"
                element={
                  <ProtectedRoute
                    pageName="/applications"
                    dropdownconfigControl={applicationConfigControl}
                    Component={<Applications />}
                  />
                }
              />
              <Route
                exact
                path="/before-after"
                element={
                  <ProtectedRoute
                    pageName="/before-after"
                    moduleName={BEFORE_AFTER}
                    Component={<BeforeAfter />}
                  />
                }
              />
              <Route
                exact
                path="/medicine-bill"
                element={<ProtectedRoute pageName="/medicine-bill" Component={<MedicineBill />} />}
              />
              <Route
                exact
                path="/credit-notes"
                element={
                  <ProtectedRoute
                    pageName="/credit-notes"
                    moduleName={VIEW_MEDICINE_PURCHASE_RETURN}
                    Component={<CreditNotes />}
                  />
                }
              />
              <Route
                exact
                path="/treatment"
                element={
                  <ProtectedRoute
                    pageName="/treatment"
                    moduleName={TREATMENT}
                    Component={<Treatments />}
                  />
                }
              />
            </Route>
          </Routes>
        )}
      </Suspense>
    </div>
  );
}

export default App;
